import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Alert,
  IconButton,
  Grid,
  Paper,
} from '@mui/material';
import {
  ExpandMore,
  History,
  NewReleases,
  BugReport,
  Build,
  MenuBook,
  Palette,
  Rocket,
  Feedback,
  Info,
  Close,
  Timeline as TimelineIcon,
  Assessment,
  Group,
  Security,
  Speed,
  IntegrationInstructions,
  Language,
  Notifications,
} from '@mui/icons-material';

interface ChangeLogProps {
  open: boolean;
  onClose: () => void;
}

const ChangeLog: React.FC<ChangeLogProps> = ({ open, onClose }) => {
  const [expandedSection, setExpandedSection] = useState<string | false>('panel1');

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedSection(isExpanded ? panel : false);
  };

  const versionData = [
    {
      version: '2.0',
      date: 'March 2026',
      type: 'major',
      icon: <Rocket color="primary" />,
      color: 'primary',
      features: [
        '📖 Application User Guide: Comprehensive system documentation',
        '🎫 Ticketing User Guide: Complete support system guide',
      ],
    },
    {
      version: '1.5',
      date: 'February 2026',
      type: 'minor',
      icon: <Build color="warning" />,
      color: 'warning',
      features: [
        '🔄 Issue Lifecycle: Fixed status transition workflows',
        '📊 Progress Timeline: Visual progress tracking',
        '👥 Role Assignments: Fixed per-event role system',
      ],
      fixes: [
        '🐛 Issue status transition fixes',
        '🔐 Permission update problems',
        '📧 Email notification accuracy',
      ],
    },
    {
      version: '1.4',
      date: 'January 2026',
      type: 'minor',
      icon: <NewReleases color="info" />,
      color: 'info',
      features: [
        '📊 Data Analysis: Comprehensive analytics dashboard',
        '📈 Visual Reports: Charts and graphs for tracking',
        '🔍 Advanced Search: Enhanced filtering capabilities',
        '📱 Mobile Views: Improved mobile experience',
      ],
    },
    {
      version: '1.3',
      date: 'December 2025',
      type: 'minor',
      icon: <Speed color="secondary" />,
      color: 'secondary',
      improvements: [
        '🗄️ Database Optimization: Improved query performance',
        '🔐 Security Enhancements: Updated authentication',
        '📊 API Improvements: Better error handling',
        '⚡ Performance: Faster page loading',
      ],
    },
    {
      version: '1.2',
      date: 'November 2025',
      type: 'patch',
      icon: <BugReport color="error" />,
      color: 'error',
      fixes: [
        '🔐 Login Issues: Resolved authentication problems',
        '📧 Email Delivery: Fixed notification system',
        '📊 Data Display: Corrected dashboard metrics',
        '📱 Mobile Bugs: Fixed mobile-specific issues',
      ],
    },
    {
      version: '1.1',
      date: 'October 2025',
      type: 'major',
      icon: <Rocket color="success" />,
      color: 'success',
      features: [
        '🏠 Dashboard: Main event overview',
        '📊 Events Management: Create and manage events',
        '🐛 Issue Tracking: Complete issue management',
        '👥 User Management: Role-based administration',
        '🔐 Authentication System: Secure login and access',
      ],
    },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <History color="primary" />
            <Typography variant="h6">e-BIRA Change Log & History</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" paragraph>
          Track all changes, updates, and improvements made to the e-BIRA system.
        </Typography>

        {/* Current Version */}
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Current Version:</strong> 2.0 (March 2026) 
            {/* <strong> Next Release:</strong> 2.1 (Planned for April 2026) */}
          </Typography>
        </Alert>

        {/* Version History */}
        <Accordion expanded={expandedSection === 'panel1'} onChange={handleAccordionChange('panel1')}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box display="flex" alignItems="center" gap={1}>
              <TimelineIcon color="primary" />
              <Typography variant="subtitle1">📜 Version History</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {versionData.map((version) => (
                <Box key={version.version} sx={{ borderLeft: `4px solid`, borderColor: `${version.color}.main`, pl: 2, py: 1 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    {version.icon}
                    <Chip 
                      label={`v${version.version}`} 
                      size="small" 
                      color={version.color as any}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {version.date}
                    </Typography>
                  </Box>
                  
                  {version.features && version.features.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" fontWeight="bold" color="success.main">
                        New Features:
                      </Typography>
                      <List dense>
                        {version.features.map((feature, idx) => (
                          <ListItem key={idx} sx={{ py: 0 }}>
                            <ListItemText 
                              primary={feature}
                              primaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                  
                  {version.improvements && version.improvements.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" fontWeight="bold" color="info.main">
                        Improvements:
                      </Typography>
                      <List dense>
                        {version.improvements.map((improvement, idx) => (
                          <ListItem key={idx} sx={{ py: 0 }}>
                            <ListItemText 
                              primary={improvement}
                              primaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                  
                  {version.fixes && version.fixes.length > 0 && (
                    <Box>
                      <Typography variant="body2" fontWeight="bold" color="error.main">
                        Bug Fixes:
                      </Typography>
                      <List dense>
                        {version.fixes.map((fix, idx) => (
                          <ListItem key={idx} sx={{ py: 0 }}>
                            <ListItemText 
                              primary={fix}
                              primaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Change Categories */}
        <Accordion expanded={expandedSection === 'panel2'} onChange={handleAccordionChange('panel2')}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box display="flex" alignItems="center" gap={1}>
              <Info color="info" />
              <Typography variant="subtitle1">📋 Change Categories</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, height: '100%' }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <NewReleases color="primary" />
                    <Typography variant="body2" fontWeight="bold">
                      🚀 New Features
                    </Typography>
                  </Box>
                  <Typography variant="caption" component="div">
                    • Completely new functionality<br/>
                    • Major system enhancements<br/>
                    • New modules or components
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, height: '100%' }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Build color="info" />
                    <Typography variant="body2" fontWeight="bold">
                      🔧 Improvements
                    </Typography>
                  </Box>
                  <Typography variant="caption" component="div">
                    • Enhanced existing features<br/>
                    • Performance optimizations<br/>
                    • User experience improvements
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, height: '100%' }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <BugReport color="error" />
                    <Typography variant="body2" fontWeight="bold">
                      🐛 Bug Fixes
                    </Typography>
                  </Box>
                  <Typography variant="caption" component="div">
                    • Resolved system issues<br/>
                    • Security patches<br/>
                    • Stability improvements
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, height: '100%' }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <MenuBook color="success" />
                    <Typography variant="body2" fontWeight="bold">
                      📚 Documentation
                    </Typography>
                  </Box>
                  <Typography variant="caption" component="div">
                    • User guide updates<br/>
                    • API documentation<br/>
                    • Technical documentation
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Version Statistics */}
        <Accordion expanded={expandedSection === 'panel3'} onChange={handleAccordionChange('panel3')}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box display="flex" alignItems="center" gap={1}>
              <Assessment color="secondary" />
              <Typography variant="subtitle1">📊 Version Statistics</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Version</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Release Date</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Major Features</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Bug Fixes</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Documentation</th>
                  </tr>
                </thead>
                <tbody>
                  {versionData.map((version) => (
                    <tr key={version.version} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '8px' }}>
                        <Chip 
                          label={`v${version.version}`} 
                          size="small" 
                          color={version.color as any}
                        />
                      </td>
                      <td style={{ padding: '8px' }}>{version.date}</td>
                      <td style={{ padding: '8px' }}>{version.features?.length || 0}</td>
                      <td style={{ padding: '8px' }}>{version.fixes ? version.fixes.length : 0}</td>
                      <td style={{ padding: '8px' }}>
                        {version.version === '2.0' ? 'Complete' : 
                         version.version >= '1.4' ? 'Updated' : 'Basic'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
            
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" fontWeight="bold" color="secondary.main">
                Summary:
              </Typography>
              <Typography variant="caption" component="div">
                • Total Versions: {versionData.length}<br/>
                • Major Releases: {versionData.filter(v => v.type === 'major').length}<br/>
                • Feature Releases: {versionData.filter(v => v.type === 'minor').length}<br/>
                • Patch Releases: {versionData.filter(v => v.type === 'patch').length}
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Feedback & Support */}
        <Accordion expanded={expandedSection === 'panel4'} onChange={handleAccordionChange('panel4')}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box display="flex" alignItems="center" gap={1}>
              <Feedback color="success" />
              <Typography variant="subtitle1">📞 Feedback & Support</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" paragraph>
              Help us improve e-BIRA by providing feedback and reporting issues.
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" fontWeight="bold" color="success.main">
                  How to Report Issues:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText 
                      primary="🎫 Use the Ticketing System" 
                      secondary="Submit a support ticket through the application"
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="📸 Include Screenshots" 
                      secondary="Provide visual evidence when possible"
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="📝 Specify Version" 
                      secondary="Mention the version you're using"
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                </List>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="body2" fontWeight="bold" color="success.main">
                  Feature Requests:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText 
                      primary="🎫 Submit via Ticket" 
                      secondary="Use 'Feature Request' subject"
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="📋 Describe Use Case" 
                      secondary="Explain why the feature is needed"
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="🎯 Set Priority" 
                      secondary="Indicate importance level"
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
            
            <Alert severity="success" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Your feedback matters!</strong> Every suggestion helps us make e-BIRA better for everyone.
              </Typography>
            </Alert>
          </AccordionDetails>
        </Accordion>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Got it, thanks!
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangeLog;
